import { createAsyncThunk } from '@reduxjs/toolkit'
import request, { getAxiosRequestConfig, newRequest } from 'utils/request.ts'
import { Methods, NewResponse } from 'types/request.ts'
import {
  CreateSalesEventPayload,
  CreateSalesEventSuccessPayload,
  CreateSalesParameterPayload,
  CreateSalesUserPayload,
  DeleteSalesEventPayload,
  DeleteSalesParameterPayload,
  DeleteSalesUserPayload,
  GetSalesEventsSuccessPayload,
  GetSalesParametersSuccessPayload,
  GetSalesPartnersSuccessPayload,
  GetSalesUsersSuccessPayload,
  UpdateSalesEventPayload,
  UpdateSalesEventSuccessPayload,
  UpdateSalesParameterPayload,
  UpdateSalesUserPayload
} from 'store/sales/thunks/types.ts'
import { sortAlphabetical, sortByDate } from 'utils/sorting.ts'

// GET
export const getSalesEvents = createAsyncThunk<GetSalesEventsSuccessPayload>('sales/getSalesEvents', async (_, thunkAPI) => {
  try {
    const { success, data, message }: NewResponse<GetSalesEventsSuccessPayload> = await newRequest<GetSalesEventsSuccessPayload>(
      getAxiosRequestConfig(Methods.GET, '/sales/events')
    )

    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'getSalesEvents')
    }

    return { events: sortByDate(data?.events, 'createdAt') }
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

export const getSalesPartners = createAsyncThunk<GetSalesPartnersSuccessPayload>('sales/getSalesPartners', async (_, thunkAPI) => {
  try {
    const { success, data, message }: NewResponse<GetSalesPartnersSuccessPayload> = await newRequest<GetSalesPartnersSuccessPayload>(
      getAxiosRequestConfig(Methods.GET, '/partners')
    )

    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'getSalesPartners')
    }

    return { partners: sortAlphabetical(data?.partners, 'name') }
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

export const getSalesParameters = createAsyncThunk<GetSalesParametersSuccessPayload>('sales/getSalesParameters', async (_, thunkAPI) => {
  try {
    const { success, data, message } = await request(getAxiosRequestConfig(Methods.GET, '/sales/parameters'))

    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'getSalesParameters')
    }

    return { parameters: sortAlphabetical(data?.parameters, 'key') }
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

export const getSalesUsers = createAsyncThunk<GetSalesUsersSuccessPayload>('sales/getSalesUsers', async (_, thunkAPI) => {
  try {
    const { success, data, message }: NewResponse<GetSalesUsersSuccessPayload> = await newRequest<GetSalesUsersSuccessPayload>(
      getAxiosRequestConfig(Methods.GET, '/sales/users')
    )

    if (!success || !data) {
      return thunkAPI.rejectWithValue(message ?? 'getSalesUsers')
    }

    return { users: sortByDate(data.users, 'createdAt') }
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

// CREATE
export const createSalesEvent = createAsyncThunk<void, CreateSalesEventPayload>(
  'sales/createSalesEvent',
  async ({ data, navigate }, thunkAPI) => {
    try {
      const { success, message } = await newRequest<CreateSalesEventSuccessPayload>(
        getAxiosRequestConfig(Methods.POST, '/sales/events', data)
      )

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'createSalesEvent')
      }

      navigate(-1)
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const createSalesParameter = createAsyncThunk<void, CreateSalesParameterPayload>(
  'sales/createSalesParameter',
  async ({ data, navigate }, thunkAPI) => {
    try {
      const { success, message } = await request(getAxiosRequestConfig(Methods.POST, '/sales/parameter', data))

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'createSalesParameter')
      }

      navigate(-1)
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const createSalesUser = createAsyncThunk<void, CreateSalesUserPayload>(
  'sales/createSalesUser',
  async ({ data, navigate }, thunkAPI) => {
    try {
      const { success, message } = await newRequest(getAxiosRequestConfig(Methods.POST, '/sales/users', { user: data }))

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'createSalesUser')
      }

      navigate(-1)
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// UPDATE
export const updateSalesEvent = createAsyncThunk<void, UpdateSalesEventPayload>(
  'sales/updateSalesEvent',
  async ({ id, data, navigate }, thunkAPI) => {
    try {
      const { success, message } = await newRequest<UpdateSalesEventSuccessPayload>(
        getAxiosRequestConfig(Methods.PATCH, `/sales/events/${id}`, data)
      )

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'updateSalesEvent')
      }

      navigate(-1)
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const updateSalesParameter = createAsyncThunk<void, UpdateSalesParameterPayload>(
  'sales/updateSalesParameter',
  async ({ id, data, navigate }, thunkAPI) => {
    try {
      const { success, message } = await request(getAxiosRequestConfig(Methods.PATCH, `/sales/parameter/${id}`, data))

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'updateSalesParameter')
      }

      navigate(-1)
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const updateSalesUser = createAsyncThunk<void, UpdateSalesUserPayload>(
  'sales/updateSalesUser',
  async ({ id, data, navigate }, thunkAPI) => {
    try {
      const { success, message } = await newRequest(getAxiosRequestConfig(Methods.PATCH, `/sales/users/${id}`, { user: data }))

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'updateSalesUser')
      }

      navigate?.(-1)
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// DELETE
export const deleteSalesEvent = createAsyncThunk<void, DeleteSalesEventPayload>('sales/deleteSalesEvent', async ({ id }, thunkAPI) => {
  try {
    const { success, message } = await request(getAxiosRequestConfig(Methods.DELETE, `/sales/events/${id}`))
    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'deleteSalesEvent')
    }

    thunkAPI.dispatch(getSalesEvents())
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

export const deleteSalesParameter = createAsyncThunk<void, DeleteSalesParameterPayload>(
  'sales/deleteSalesParameter',
  async ({ id }, thunkAPI) => {
    try {
      const { success, message } = await request(getAxiosRequestConfig(Methods.DELETE, `/sales/parameter/${id}`))
      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'deleteSalesParameter')
      }

      thunkAPI.dispatch(getSalesParameters())
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const deleteSalesUser = createAsyncThunk<void, DeleteSalesUserPayload>('sales/deleteSalesUser', async ({ id }, thunkAPI) => {
  try {
    const { success, message } = await newRequest(getAxiosRequestConfig(Methods.DELETE, `/sales/users/${id}`))

    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'deleteSalesUser')
    }

    thunkAPI.dispatch(getSalesUsers())
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})
